/**
 * File Store
 *
 * Manages file system state using Zustand.
 * Provides directory tree browsing, open file management, and file operations.
 *
 * Usage:
 *   import { useFileStore } from '@/stores/fileStore';
 *   const { tree, openFiles, loadTree, openFile } = useFileStore();
 */

import { create } from 'zustand';
import {
  api,
  TreeNode,
  FileReadResponse,
  ApiRequestError,
} from '@/services/api';

// ============================================
// Types
// ============================================

/**
 * Represents an open file with its content loaded
 */
export interface OpenFile {
  path: string;
  name: string;
  content: string | null; // null for binary files
  mimeType: string;
  size: number;
  isModified: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * File type categories for viewer selection
 */
export type FileViewerType =
  | 'code'
  | 'csv'
  | 'parquet'
  | 'excel'
  | 'html'
  | 'markdown'
  | 'image'
  | 'pdf'
  | 'pcap'
  | 'text'
  | 'binary';

export interface FileState {
  // Directory tree state
  tree: TreeNode[];
  treePath: string;
  treeLoading: boolean;
  treeError: string | null;

  // Expanded directories (for tree view state persistence)
  expandedDirs: Set<string>;

  // Selected paths in tree (for highlighting, multi-select)
  selectedPaths: Set<string>;
  lastSelectedPath: string | null; // For Shift+Click range selection

  // Open files state
  openFiles: OpenFile[];
  activeFilePath: string | null;

  // Explicit open-intent signal. Bumped only when the user deliberately opens
  // a file via openFile() (tree click, pin, produced, etc.) - including when
  // that file is already open. Lets the layout expand a minimized viewer on an
  // explicit open without reacting to passive activeFilePath changes (close/
  // delete fallback, tab switches). See Layout.tsx MainContent.
  openRequestNonce: number;

  // Settings
  showHiddenFiles: boolean;

  // Actions - Tree
  loadTree: (path?: string, depth?: number) => Promise<void>;
  refreshTree: () => Promise<void>;
  toggleDir: (path: string) => void;
  expandDir: (path: string) => void;
  collapseDir: (path: string) => void;
  // Selection actions
  selectPath: (path: string | null) => void; // Single select (replaces all)
  togglePathSelection: (path: string) => void; // Ctrl/Cmd+Click
  selectRange: (path: string, allPaths: string[]) => void; // Shift+Click
  clearSelection: () => void;
  toggleHiddenFiles: () => Promise<void>;

  // Actions - Open Files
  openFile: (path: string) => Promise<void>;
  closeFile: (path: string) => void;
  closeAllFiles: () => void;
  closeOtherFiles: (path: string) => void;
  setActiveFile: (path: string | null) => void;
  reloadFile: (path: string) => Promise<void>;
  setFileContent: (path: string, content: string) => void;
  markFileModified: (path: string, isModified: boolean) => void;

  // Actions - File Operations
  createDirectory: (path: string) => Promise<boolean>;
  createFile: (path: string, content?: string) => Promise<boolean>;
  deleteItem: (path: string, recursive?: boolean) => Promise<boolean>;
  moveItem: (source: string, destination: string) => Promise<boolean>;
  uploadFiles: (files: File[], destination?: string) => Promise<boolean>;

  // Utilities
  getFileByPath: (path: string) => OpenFile | undefined;
  getViewerType: (path: string) => FileViewerType;
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Helpers
// ============================================

/**
 * Get file extension from path
 */
function getExtension(path: string): string {
  const lastDot = path.lastIndexOf('.');
  const lastSlash = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
  if (lastDot > lastSlash && lastDot > -1) {
    return path.slice(lastDot + 1).toLowerCase();
  }
  return '';
}

/**
 * Get file name from path
 */
function getFileName(path: string): string {
  const lastSlash = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
  return lastSlash > -1 ? path.slice(lastSlash + 1) : path;
}

/**
 * Determine viewer type based on file extension and MIME type
 */
function determineViewerType(
  path: string,
  mimeType?: string
): FileViewerType {
  const ext = getExtension(path);

  // CSV files
  if (ext === 'csv' || ext === 'tsv') {
    return 'csv';
  }

  if (ext === 'parquet') {
    return 'parquet';
  }

  // Modern Excel files
  if (ext === 'xlsx' || ext === 'xlsm') {
    return 'excel';
  }

  // PCAP files
  if (ext === 'pcap' || ext === 'pcapng' || ext === 'cap') {
    return 'pcap';
  }

  // Markdown files
  if (ext === 'md' || ext === 'markdown') {
    return 'markdown';
  }

  // PDF files
  if (ext === 'pdf') {
    return 'pdf';
  }

  // HTML files (with preview capability)
  if (ext === 'html' || ext === 'htm') {
    return 'html';
  }

  // Image files
  const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'bmp'];
  if (imageExts.includes(ext)) {
    return 'image';
  }

  // Code files (syntax highlighting)
  const codeExts = [
    'js',
    'jsx',
    'ts',
    'tsx',
    'json',
    'py',
    'rs',
    'go',
    'java',
    'c',
    'cpp',
    'h',
    'hpp',
    'css',
    'scss',
    'less',
    'html',
    'htm',
    'xml',
    'yaml',
    'yml',
    'toml',
    'ini',
    'conf',
    'sh',
    'bash',
    'zsh',
    'sql',
    'graphql',
    'dockerfile',
    'makefile',
    'gitignore',
    'env',
  ];
  if (codeExts.includes(ext)) {
    return 'code';
  }

  // Plain text files
  const textExts = ['txt', 'log', 'readme', 'license', 'changelog'];
  if (textExts.includes(ext)) {
    return 'text';
  }

  // Check MIME type fallback
  if (mimeType) {
    if (mimeType.startsWith('text/')) {
      return 'text';
    }
    if (mimeType.startsWith('image/')) {
      return 'image';
    }
    if (mimeType === 'application/pdf') {
      return 'pdf';
    }
    if (mimeType === 'application/json') {
      return 'code';
    }
  }

  // Default to binary for unknown types
  return 'binary';
}

/**
 * Check if file type should load content as text
 */
function shouldLoadAsText(viewerType: FileViewerType): boolean {
  return ['code', 'csv', 'markdown', 'text', 'html'].includes(viewerType);
}

// ============================================
// Initial State
// ============================================

const initialState = {
  tree: [] as TreeNode[],
  treePath: '',
  treeLoading: false,
  treeError: null as string | null,
  expandedDirs: new Set<string>(),
  selectedPaths: new Set<string>(),
  lastSelectedPath: null as string | null,
  openFiles: [] as OpenFile[],
  activeFilePath: null as string | null,
  openRequestNonce: 0,
  showHiddenFiles: false,
};

// ============================================
// Store
// ============================================

export const useFileStore = create<FileState>((set, get) => ({
  ...initialState,

  // ----------------------------------------
  // Tree Actions
  // ----------------------------------------

  /**
   * Load directory tree from the server
   */
  loadTree: async (path = '', depth = 10) => {
    const state = get();
    if (state.treeLoading) return;

    set({ treeLoading: true, treeError: null });

    try {
      const response = await api.files.getTree(path, { depth, showHidden: state.showHiddenFiles });

      // TypeScript guard - getTree can return either tree or flat list
      if ('tree' in response) {
        set({
          tree: response.tree,
          treePath: response.path,
          treeLoading: false,
        });
      } else {
        // If flat response, convert to tree nodes (no children)
        const treeNodes: TreeNode[] = response.entries.map((entry) => ({
          ...entry,
          children: entry.type === 'directory' ? [] : undefined,
        }));
        set({
          tree: treeNodes,
          treePath: response.path,
          treeLoading: false,
        });
      }
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load directory tree';

      set({
        treeLoading: false,
        treeError: message,
      });
    }
  },

  /**
   * Refresh the current tree
   */
  refreshTree: async () => {
    const { treePath } = get();
    // Match the depth used by the initial mount load (FileTree passes 20).
    // Falling back to loadTree's smaller default here would silently drop any
    // nodes deeper than that on every post-mutation/upload refresh.
    await get().loadTree(treePath, 20);
  },

  /**
   * Toggle directory expansion
   */
  toggleDir: (path: string) => {
    set((state) => {
      const newExpanded = new Set(state.expandedDirs);
      if (newExpanded.has(path)) {
        newExpanded.delete(path);
      } else {
        newExpanded.add(path);
      }
      return { expandedDirs: newExpanded };
    });
  },

  /**
   * Expand a directory
   */
  expandDir: (path: string) => {
    set((state) => {
      const newExpanded = new Set(state.expandedDirs);
      newExpanded.add(path);
      return { expandedDirs: newExpanded };
    });
  },

  /**
   * Collapse a directory
   */
  collapseDir: (path: string) => {
    set((state) => {
      const newExpanded = new Set(state.expandedDirs);
      newExpanded.delete(path);
      return { expandedDirs: newExpanded };
    });
  },

  /**
   * Select a single path in the tree (replaces existing selection)
   */
  selectPath: (path: string | null) => {
    if (path === null) {
      set({ selectedPaths: new Set(), lastSelectedPath: null });
    } else {
      set({ selectedPaths: new Set([path]), lastSelectedPath: path });
    }
  },

  /**
   * Toggle a path's selection (Ctrl/Cmd+Click behavior)
   */
  togglePathSelection: (path: string) => {
    const { selectedPaths } = get();
    const newSelected = new Set(selectedPaths);
    if (newSelected.has(path)) {
      newSelected.delete(path);
    } else {
      newSelected.add(path);
    }
    set({ selectedPaths: newSelected, lastSelectedPath: path });
  },

  /**
   * Select a range of paths (Shift+Click behavior)
   * allPaths should be the flat list of visible paths in order
   */
  selectRange: (path: string, allPaths: string[]) => {
    const { lastSelectedPath, selectedPaths } = get();

    if (!lastSelectedPath) {
      // No previous selection, just select this one
      set({ selectedPaths: new Set([path]), lastSelectedPath: path });
      return;
    }

    const startIndex = allPaths.indexOf(lastSelectedPath);
    const endIndex = allPaths.indexOf(path);

    if (startIndex === -1 || endIndex === -1) {
      // One of the paths not found, just select the clicked one
      set({ selectedPaths: new Set([path]), lastSelectedPath: path });
      return;
    }

    // Select all paths in the range
    const min = Math.min(startIndex, endIndex);
    const max = Math.max(startIndex, endIndex);
    const rangePaths = allPaths.slice(min, max + 1);

    // Add to existing selection
    const newSelected = new Set(selectedPaths);
    rangePaths.forEach(p => newSelected.add(p));

    set({ selectedPaths: newSelected });
    // Note: don't update lastSelectedPath on range select
  },

  /**
   * Clear all selection
   */
  clearSelection: () => {
    set({ selectedPaths: new Set(), lastSelectedPath: null });
  },

  /**
   * Toggle visibility of hidden files (files starting with .)
   * Reloads the tree to fetch hidden files from server
   */
  toggleHiddenFiles: async () => {
    const newShowHidden = !get().showHiddenFiles;
    set({ showHiddenFiles: newShowHidden });
    // Reload tree with new showHidden setting
    await get().refreshTree();
  },

  // ----------------------------------------
  // Open Files Actions
  // ----------------------------------------

  /**
   * Open a file and load its content
   */
  openFile: async (path: string) => {
    const state = get();

    // Check if file is already open
    const existingFile = state.openFiles.find((f) => f.path === path);
    if (existingFile) {
      // Already open: make it active, select it, and bump the open-intent nonce
      // so a minimized viewer expands (openFiles.length doesn't change here).
      set({
        activeFilePath: path,
        selectedPaths: new Set([path]),
        lastSelectedPath: path,
        openRequestNonce: state.openRequestNonce + 1,
      });
      return;
    }

    // Determine viewer type before loading
    const viewerType = determineViewerType(path);
    const fileName = getFileName(path);

    // Create placeholder entry
    const newFile: OpenFile = {
      path,
      name: fileName,
      content: null,
      mimeType: '',
      size: 0,
      isModified: false,
      isLoading: true,
      error: null,
    };

    // Add to open files and set as active. Bump the open-intent nonce alongside
    // the new tab so the layout expands the viewer on this explicit open.
    set((state) => ({
      openFiles: [...state.openFiles, newFile],
      activeFilePath: path,
      selectedPaths: new Set([path]),
      lastSelectedPath: path,
      openRequestNonce: state.openRequestNonce + 1,
    }));

    // Load content if it's a text-based file
    if (shouldLoadAsText(viewerType)) {
      try {
        const response: FileReadResponse = await api.files.read(path);

        set((state) => ({
          openFiles: state.openFiles.map((f) =>
            f.path === path
              ? {
                  ...f,
                  content: response.content,
                  mimeType: response.mimeType,
                  size: response.size,
                  isLoading: false,
                }
              : f
          ),
        }));
      } catch (err) {
        const message =
          err instanceof ApiRequestError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load file';

        set((state) => ({
          openFiles: state.openFiles.map((f) =>
            f.path === path
              ? {
                  ...f,
                  isLoading: false,
                  error: message,
                }
              : f
          ),
        }));
      }
    } else {
      // For binary files (images, PDF), just mark as loaded
      // The viewer components will use download URL
      try {
        const stat = await api.files.stat(path);
        set((state) => ({
          openFiles: state.openFiles.map((f) =>
            f.path === path
              ? {
                  ...f,
                  size: stat.size,
                  isLoading: false,
                }
              : f
          ),
        }));
      } catch (err) {
        const message =
          err instanceof ApiRequestError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to get file info';

        set((state) => ({
          openFiles: state.openFiles.map((f) =>
            f.path === path
              ? {
                  ...f,
                  isLoading: false,
                  error: message,
                }
              : f
          ),
        }));
      }
    }
  },

  /**
   * Close an open file
   */
  closeFile: (path: string) => {
    set((state) => {
      const newOpenFiles = state.openFiles.filter((f) => f.path !== path);

      // If closing the active file, activate the next one
      let newActivePath = state.activeFilePath;
      if (state.activeFilePath === path) {
        const closingIndex = state.openFiles.findIndex((f) => f.path === path);
        if (newOpenFiles.length > 0) {
          // Prefer the file to the right, or the one to the left
          const newIndex = Math.min(closingIndex, newOpenFiles.length - 1);
          newActivePath = newOpenFiles[newIndex].path;
        } else {
          newActivePath = null;
        }
      }

      return {
        openFiles: newOpenFiles,
        activeFilePath: newActivePath,
      };
    });
  },

  /**
   * Close all open files
   */
  closeAllFiles: () => {
    set({
      openFiles: [],
      activeFilePath: null,
    });
  },

  /**
   * Close all files except the specified one
   */
  closeOtherFiles: (path: string) => {
    set((state) => ({
      openFiles: state.openFiles.filter((f) => f.path === path),
      activeFilePath: path,
    }));
  },

  /**
   * Set the active file tab
   */
  setActiveFile: (path: string | null) => {
    if (path === null) {
      set({ activeFilePath: null });
    } else {
      set({ activeFilePath: path, selectedPaths: new Set([path]), lastSelectedPath: path });
    }
  },

  /**
   * Reload a file's content from the server
   */
  reloadFile: async (path: string) => {
    const state = get();
    const file = state.openFiles.find((f) => f.path === path);
    if (!file) return;

    const viewerType = determineViewerType(path);

    // Mark as loading
    set((state) => ({
      openFiles: state.openFiles.map((f) =>
        f.path === path ? { ...f, isLoading: true, error: null } : f
      ),
    }));

    if (shouldLoadAsText(viewerType)) {
      try {
        const response: FileReadResponse = await api.files.read(path);

        set((state) => ({
          openFiles: state.openFiles.map((f) =>
            f.path === path
              ? {
                  ...f,
                  content: response.content,
                  mimeType: response.mimeType,
                  size: response.size,
                  isLoading: false,
                  isModified: false,
                }
              : f
          ),
        }));
      } catch (err) {
        const message =
          err instanceof ApiRequestError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to reload file';

        set((state) => ({
          openFiles: state.openFiles.map((f) =>
            f.path === path ? { ...f, isLoading: false, error: message } : f
          ),
        }));
      }
    } else {
      // For binary files, just refresh metadata
      set((state) => ({
        openFiles: state.openFiles.map((f) =>
          f.path === path ? { ...f, isLoading: false, isModified: false } : f
        ),
      }));
    }
  },

  /**
   * Update file content locally (for editing)
   */
  setFileContent: (path: string, content: string) => {
    set((state) => ({
      openFiles: state.openFiles.map((f) =>
        f.path === path ? { ...f, content, isModified: true } : f
      ),
    }));
  },

  /**
   * Mark a file as modified or not
   */
  markFileModified: (path: string, isModified: boolean) => {
    set((state) => ({
      openFiles: state.openFiles.map((f) =>
        f.path === path ? { ...f, isModified } : f
      ),
    }));
  },

  // ----------------------------------------
  // File Operations
  // ----------------------------------------

  /**
   * Create a new directory
   */
  createDirectory: async (path: string) => {
    try {
      await api.files.mkdir(path);
      // Refresh tree to show new directory
      await get().refreshTree();
      return true;
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to create directory';
      set({ treeError: message });
      return false;
    }
  },

  /**
   * Create a new file
   */
  createFile: async (path: string, content = '') => {
    try {
      await api.files.write(path, content);
      // Refresh tree to show new file
      await get().refreshTree();
      return true;
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to create file';
      set({ treeError: message });
      return false;
    }
  },

  /**
   * Delete a file or directory
   */
  deleteItem: async (path: string, recursive = false) => {
    try {
      await api.files.delete(path, { recursive });

      // Close file if it was open
      const state = get();
      if (state.openFiles.some((f) => f.path === path)) {
        get().closeFile(path);
      }

      // Refresh tree
      await get().refreshTree();
      return true;
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to delete';
      set({ treeError: message });
      return false;
    }
  },

  /**
   * Move or rename a file/directory
   */
  moveItem: async (source: string, destination: string) => {
    try {
      await api.files.move(source, destination);

      // Update open file path if it was moved
      set((state) => ({
        openFiles: state.openFiles.map((f) => {
          if (f.path === source) {
            return {
              ...f,
              path: destination,
              name: getFileName(destination),
            };
          }
          // Also update files that were inside a moved directory
          if (f.path.startsWith(source + '/')) {
            const newPath = destination + f.path.slice(source.length);
            return {
              ...f,
              path: newPath,
            };
          }
          return f;
        }),
        activeFilePath:
          state.activeFilePath === source
            ? destination
            : state.activeFilePath?.startsWith(source + '/')
              ? destination + state.activeFilePath.slice(source.length)
              : state.activeFilePath,
        // Update selected paths that were affected by the move
        selectedPaths: new Set(
          Array.from(state.selectedPaths).map((p) => {
            if (p === source) return destination;
            if (p.startsWith(source + '/')) return destination + p.slice(source.length);
            return p;
          })
        ),
        lastSelectedPath:
          state.lastSelectedPath === source
            ? destination
            : state.lastSelectedPath?.startsWith(source + '/')
              ? destination + state.lastSelectedPath.slice(source.length)
              : state.lastSelectedPath,
      }));

      // Refresh tree
      await get().refreshTree();
      return true;
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to move';
      set({ treeError: message });
      return false;
    }
  },

  /**
   * Upload files
   */
  uploadFiles: async (files: File[], destination = '') => {
    try {
      const result = await api.files.upload(files, destination);

      // Refresh tree to show new files
      await get().refreshTree();

      // Return true if any files were uploaded successfully
      return result.summary.successful > 0;
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to upload files';
      set({ treeError: message });
      return false;
    }
  },

  // ----------------------------------------
  // Utilities
  // ----------------------------------------

  /**
   * Get an open file by path
   */
  getFileByPath: (path: string) => {
    return get().openFiles.find((f) => f.path === path);
  },

  /**
   * Get the viewer type for a file path
   */
  getViewerType: (path: string) => {
    const file = get().openFiles.find((f) => f.path === path);
    return determineViewerType(path, file?.mimeType);
  },

  /**
   * Clear tree error
   */
  clearError: () => {
    set({ treeError: null });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set({
      ...initialState,
      expandedDirs: new Set<string>(),
    });
  },
}));

// ============================================
// Selectors
// ============================================

/**
 * Select the directory tree
 */
export const selectTree = (state: FileState) => state.tree;

/**
 * Select tree loading state
 */
export const selectTreeLoading = (state: FileState) => state.treeLoading;

/**
 * Select tree error
 */
export const selectTreeError = (state: FileState) => state.treeError;

/**
 * Select expanded directories
 */
export const selectExpandedDirs = (state: FileState) => state.expandedDirs;

/**
 * Select the currently selected paths (Set)
 */
export const selectSelectedPaths = (state: FileState) => state.selectedPaths;

/**
 * Select the last selected path (for range selection)
 */
export const selectLastSelectedPath = (state: FileState) => state.lastSelectedPath;

/**
 * Select all open files
 */
export const selectOpenFiles = (state: FileState) => state.openFiles;

/**
 * Select the active file path
 */
export const selectActiveFilePath = (state: FileState) => state.activeFilePath;

/**
 * Select the explicit open-intent nonce (bumped on each openFile call)
 */
export const selectOpenRequestNonce = (state: FileState) => state.openRequestNonce;

/**
 * Select the active file object
 */
export const selectActiveFile = (state: FileState) => {
  if (!state.activeFilePath) return null;
  return state.openFiles.find((f) => f.path === state.activeFilePath) || null;
};

/**
 * Check if any files are modified
 */
export const selectHasModifiedFiles = (state: FileState) =>
  state.openFiles.some((f) => f.isModified);

/**
 * Select show hidden files setting
 */
export const selectShowHiddenFiles = (state: FileState) => state.showHiddenFiles;

export default useFileStore;
